import {h} from 'preact'
import {BodyUsingKeyboard} from '@accessible/using-keyboard'
import {Drawer, Trigger, Target} from '@accessible/drawer'
import {Provider as PrerenderProvider} from '@preact/prerender-data-provider'
import {Router, Switch, Link} from 'wouter'
import css from 'minify-css.macro'
import {Grid, Row, Column, Box} from '@dash-ui/react-layout'
import {ReactComponent as Logo} from './assets/logo.svg'
import {DesignSystem, Icon, MainMenu} from './components'
import {useVh} from './hooks'
import {styles} from './styles'
// Routes are automagically code split
import {pages} from './pages'
import type {Styles} from '@dash-ui/styles'

let extractStyles: (app: JSX.Element, styles: Styles) => JSX.Element

if (__SERVER__) {
  const renderer = require('preact-render-to-string').default
  extractStyles = (app, styles) =>
    require('@dash-ui/react/server').toComponent(renderer(app), styles, {
      clearCache: false,
    })
}

const App = (props: any) => {
  let useStaticLocation: () => [string, () => void] | undefined
  // Sets a --vh variable which contains the cross-browser viewport height
  useVh()

  if (__SERVER__) {
    useStaticLocation = (): [string, () => void] => [
      props.CLI_DATA.preRenderData.url,
      () => {},
    ]
  }

  const app = (
    <PrerenderProvider props={props}>
      <Router hook={useStaticLocation}>
        <DesignSystem>
          <Drawer>
            <BodyUsingKeyboard />

            <Grid
              height='var(--vh)'
              cols={{
                phone: 1,
                tablet: [196, 'auto'],
              }}
              rows={{
                phone: [64, '1fr'],
                tablet: 1,
              }}
            >
              <Column
                height={{phone: 64, tablet: '100%'}}
                bg='ui/primary'
                style={{overflow: 'auto'}}
              >
                <Row
                  pad={{
                    phone: ['md', 'none', 'md', 'lg'],
                    tablet: ['md', 'md', 'md', 'lg'],
                  }}
                  height={64}
                  align='center'
                  distribute='between'
                >
                  <Link href='/'>
                    <a className={logo()}>
                      <Icon render={Logo} size='1.667em' />
                    </a>
                  </Link>
                  <Box display={{phone: 'block', tablet: 'none'}}>
                    <Trigger
                      closedClass={hamburger('closed')}
                      openClass={hamburger('open')}
                    >
                      <button aria-label='Main menu'>
                        <span />
                        <span />
                      </button>
                    </Trigger>
                  </Box>
                </Row>

                <Box display={{phone: 'none', tablet: 'block'}}>
                  <MainMenu />
                </Box>
              </Column>

              <Column style={{overflow: 'auto'}}>
                <Switch>{pages}</Switch>
              </Column>

              <Target placement='bottom'>
                <MainMenu variant='drawer' />
              </Target>
            </Grid>
          </Drawer>
        </DesignSystem>
      </Router>
    </PrerenderProvider>
  )

  return (
    <div id='app'>
      {extractStyles?.(app, styles)}
      {app}
    </div>
  )
}

const logo = styles.one(
  ({color}) => css`
    color: ${color['text/primary']};
  `
)

const hamburger = styles({
  default: ({color, transition, radius}) => css`
    all: unset;
    display: flex;
    flex-wrap: wrap;
    align-content: center;
    justify-content: center;
    user-select: none;
    height: 64px;
    width: 20px;
    padding: 0 36px;

    > span {
      display: block;
      height: 2px;
      background-color: ${color['ui/primaryInverse']};
      border-radius: ${radius.max};
      width: 20px;
      transform-origin: center;
      transition-property: transform, width, margin;
      transition-duration: ${transition.duration.primary};
      transition-timing-function: ${transition.timing.move};
    }
  `,
  closed: ({gap}) => css`
    > span {
      transform: rotate(0);
    }

    > span + span {
      margin-top: ${gap.sm};
    }
  `,
  open: css`
    > span {
      position: absolute;
      left: 0;
      right: 0;
      margin-left: auto;
      margin-right: auto;

      &:nth-child(odd) {
        transform: rotate(135deg);
      }

      &:nth-child(even) {
        transform: rotate(-135deg);
      }
    }

    > span + span {
      margin-top: 0;
    }
  `,
})

export default App
